/**
 * PublicLeagueArticlePage — /l/:slug/news/:postId
 * ─────────────────────────────────────────────────────────────────
 * Public pages overhaul P3 (2026-06-09). One full article — league
 * news or a team-page post (the same endpoint serves both; team
 * posts link back to their team page).
 */

import { useEffect, useState } from 'react';
import { Link, useOutletContext, useParams } from 'react-router-dom';
import publicApi from '../../services/publicApi.js';
import { renderMarkdown } from '../../utils/tinyMarkdown.jsx';
import { container, Card, Chip, MUTED, INK, formatDateNice } from './pubUi.jsx';

export default function PublicLeagueArticlePage() {
  const { slug, postId } = useParams();
  const { league, accent } = useOutletContext();
  const [post, setPost] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setPost(null); setMissing(false);
    publicApi.get(`/leagues/${encodeURIComponent(slug)}/posts/${postId}`)
      .then(({ data }) => { if (!cancelled) setPost(data?.post || null); })
      .catch(() => { if (!cancelled) setMissing(true); });
    return () => { cancelled = true; };
  }, [slug, postId]);

  useEffect(() => {
    if (post?.title && league?.name) document.title = `${post.title} — ${league.name}`;
  }, [post?.title, league?.name]);

  if (missing) {
    return (
      <div style={{ ...container, paddingTop: 60, textAlign: 'center', color: MUTED }}>
        <div style={{ fontSize: 34 }}>📰</div>
        <h1 style={{ color: INK }}>Article not found</h1>
        <Link to={`/l/${slug}/news`} style={{ color: accent, fontWeight: 800, textDecoration: 'none' }}>← Back to news</Link>
      </div>
    );
  }
  if (!post) {
    return <div style={{ ...container, paddingTop: 60, color: MUTED }}>Loading…</div>;
  }

  return (
    <div style={{ ...container, paddingTop: 36, maxWidth: 860 }}>
      <Link to={post.team_id ? `/l/${slug}/team/${post.team_id}` : `/l/${slug}/news`}
            style={{ color: accent, fontWeight: 800, textDecoration: 'none', fontSize: 14 }}>
        ← {post.team_id ? `Back to ${post.team_name || 'team'}` : 'All news'}
      </Link>

      <h1 style={{ fontSize: 'clamp(28px, 4.5vw, 42px)', fontWeight: 900, letterSpacing: '-0.02em', margin: '14px 0 10px', lineHeight: 1.12, color: INK }}>
        {post.title}
      </h1>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 24, flexWrap: 'wrap' }}>
        {post.pinned && <Chip color="#9a6700" bg="#fff3d1">Pinned</Chip>}
        {post.team_name && <Chip>{post.team_name}</Chip>}
        <span style={{ fontSize: 13, color: MUTED }}>
          {formatDateNice(post.created_at)}
          {post.author_name ? ` · by ${post.author_name}` : ''}
        </span>
      </div>

      {post.image_url && (
        <img src={post.image_url} alt=""
             style={{ width: '100%', maxHeight: 460, objectFit: 'cover', borderRadius: 18, marginBottom: 26, display: 'block' }} />
      )}

      <Card style={{ padding: '30px 32px', fontSize: 16, lineHeight: 1.7 }}>
        {post.body_markdown
          ? renderMarkdown(post.body_markdown)
          : <p style={{ color: MUTED }}>No further details.</p>}
      </Card>
    </div>
  );
}
